import React from 'react';
import styled from 'styled-components';
import { Card, InlineList } from '../../components/Layout';
import { TrainingPoster } from '../../components/TrainingElements';
import WhiteLabel from '../../filters/WhiteLabel';
import useOrg from '../../hooks/useOrg';
import trainingService from '../../services/trainingService';
import { Grid, Type } from '../../app/StyleGuide';
import TrainingOverview from '../training/components/TrainingOverview';
import TrainingPosterPlaceholder from '../training/components/TrainingPosterPlaceholder';
import { getNumTrainingItemsText } from '../training/utils/getNumTrainingItemsText';
import { TrainingItem } from './models/trainingItem';
import { IconBadge } from '../../components/Badge';

const TrainingListItemContainer = styled(Card)`
  display: flex;
  flex-direction: row;
  gap: ${Grid._4};
  padding: 0;
  margin-bottom: ${Grid._4};
  overflow: hidden;
`;

const TrainingDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._3};
  padding: ${Grid._4};
`;

const TrainingName = styled.h1`
  margin: 0;
  color: ${Type.Color.dark};
  font-size: ${Type.Scale._5};
  font-weight: ${Type.Weight.bold};
  line-height: ${Type.Leading.normal};
`;

const TrainingPosterContainer = styled.div`
  position: relative;
  width: 350px;

  & > div.sixteen-nine-aspect-ratio > div {
    border-radius: 0;
  }

  & > img {
    border-radius: 0;
  }

  span {
    position: absolute;
    bottom: 20px;
    right: -30px;
  }
`;

const TrainingListItem = ({
  training,
  redirectToTraining,
}: {
  training: TrainingItem;
  redirectToTraining: (id: string) => void;
}) => {
  const org = useOrg();
  const isOrgOwned = !!org && org.id === training.ownerId;
  const trainingOwnerName = isOrgOwned ? org.name : 'Ministry Grid';

  return (
    <TrainingListItemContainer data-qa-hook="trainingItem" onClick={() => redirectToTraining(training.id)}>
      <div>
        <TrainingPosterContainer>
          {training.image ? (
            <TrainingPoster src={trainingService.getTrainingImageUrl(training.image)} alt={training.name} />
          ) : (
            <TrainingPosterPlaceholder ownerName={isOrgOwned ? org.name : WhiteLabel.name} />
          )}
          {training._type.toLowerCase() === 'pathway' && (
            <IconBadge className="icon-badge">
              <i className="fas fa-route"></i>
            </IconBadge>
          )}
        </TrainingPosterContainer>
      </div>

      <TrainingDetailsContainer>
        <TrainingName data-qa-hook="trainingName">{training.name}</TrainingName>
        <InlineList content="•" fontSize="14px">
          <li>
            {getNumTrainingItemsText(training)} {Number(getNumTrainingItemsText(training)) > 1 && 's'}
          </li>
          {trainingOwnerName && <li>{trainingOwnerName}</li>}
        </InlineList>
        {training.description && <TrainingOverview description={training.description} maxLines={3} />}
      </TrainingDetailsContainer>
    </TrainingListItemContainer>
  );
};

export default TrainingListItem;
